/**
 * Remembers which VM expiry notices a customer has already dismissed.
 *
 * One entry per machine, keyed by resource id and holding the `expiresAt` the notice was
 * dismissed for. A customer with twenty machines dismisses twenty notices independently, and
 * renewing one moves its `expiresAt`, so the next term's notice appears again rather than staying
 * silenced forever.
 *
 * Everything lives under a single localStorage key: twenty machines would otherwise be twenty
 * keys with no way to prune them.
 */

const STORAGE_KEY = "wicloud_vm_expiry_notice_dismissed";

/** How long after a term ends its entry is kept, so a renewed machine's old row does not linger. */
const RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

/** Resource id -> the `expiresAt` value the notice was dismissed for. */
type DismissalMap = Record<string, string>;

/** Throws away a value this version cannot read, so the next read starts from a clean slate. */
function discard(reason: string, cause?: unknown): void {
  console.warn(
    `[ExpiryNotice] Discarding unreadable dismissal cache: ${reason}`,
    cause ?? "",
  );

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // Storage rejected the delete too. Nothing left to try: every read from here on hits the
    // same bad value, logs, and falls back to "nothing dismissed" — the notice shows again,
    // which is the harmless direction to fail in.
    console.warn("[ExpiryNotice] Could not remove the dismissal cache", error);
  }
}

/**
 * Reads the map.
 *
 * Two different failures, handled differently. Storage being unavailable (a private window,
 * blocked site data) is not this key's fault and leaves nothing to clean up. A value that IS
 * there but cannot be read — truncated JSON, a shape an older or newer version wrote — is
 * dropped, because keeping it means logging the same failure on every render for the life of
 * the browser profile.
 *
 * Both end at "nothing dismissed": showing a notice one extra time is harmless, throwing on a
 * detail page is not.
 */
function read(): DismissalMap {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn("[ExpiryNotice] Dismissal cache is unreadable", error);
    return {};
  }

  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    discard("not valid JSON", error);
    return {};
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    discard(`expected an object, got ${Array.isArray(parsed) ? "an array" : typeof parsed}`);
    return {};
  }

  // Every value must be the ISO expiry the notice was dismissed for. One that is not means the
  // whole value was written by something that did not agree with this shape, so it goes rather
  // than being half-trusted.
  const result: DismissalMap = {};
  for (const [id, expiresAt] of Object.entries(parsed)) {
    if (typeof expiresAt !== "string") {
      discard(`entry "${id}" is ${typeof expiresAt}, not a string`);
      return {};
    }
    result[id] = expiresAt;
  }

  return result;
}

/** Drops entries whose term ended long enough ago that the machine is gone or long renewed. */
function prune(map: DismissalMap, now: number): DismissalMap {
  const result: DismissalMap = {};
  for (const [id, expiresAt] of Object.entries(map)) {
    const time = new Date(expiresAt).getTime();

    // An unparseable date has no age to judge, so it is kept — pruning it would make the notice
    // it silenced come back on every visit.
    if (Number.isNaN(time) || now - time < RETENTION_MS) {
      result[id] = expiresAt;
    }
  }
  return result;
}

/**
 * Whether this machine's notice for this exact term has already been dismissed. A different
 * `expiresAt` — a renewal — counts as a new term and is not dismissed.
 */
export function isExpiryNoticeDismissed(
  resourceId: string,
  expiresAt: string,
): boolean {
  return read()[resourceId] === expiresAt;
}

/** Records that the customer closed this machine's notice for this term. */
export function dismissExpiryNotice(resourceId: string, expiresAt: string): void {
  try {
    const next = prune(read(), Date.now());
    next[resourceId] = expiresAt;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable or full. The notice simply reappears on the next visit; nothing else
    // in the page depends on this write.
  }
}

/** Exported for tests that need to start from a known state. */
export const EXPIRY_NOTICE_STORAGE_KEY = STORAGE_KEY;
